/*
 * Which bridge the phone talks to. config.js decides, in this order:
 *   window.BRIDGE_URL        a fixed bridge (tools/connector_shim.py)
 *   window.BRIDGE_DIRECTORY  the front door (https://bridge.telqon.com): the phone is the same for every customer,
 *                            and <directory><first 15 characters of the Salesforce org ID>.json names the customer's
 *                            bridge, e.g. {"bridge": "https://acme.bridge.telqon.com"}
 *   neither                  the bridge serving this page
 * The connector looks the bridge up at init and remembers it (with the org ID) for the login page, which Salesforce
 * opens separately on the same origin.
 */
const FIXED = String(window.BRIDGE_URL || '').replace(/\/$/, '');
const DIRECTORY = window.BRIDGE_DIRECTORY || '';
const STORE_KEY = 'telqonBridge';

let current = FIXED || (DIRECTORY ? '' : window.location.origin);
let orgId = '';
const waiting = [];

export const usesDirectory = () => !FIXED && !!DIRECTORY;
export const bridgeUrl = () => current;
export const salesforceOrg = () => orgId;

/** Calls fn(url) now if the bridge is known, else once it is. */
export function whenBridge(fn) {
    if (current) fn(current); else waiting.push(fn);
}

function use(url, org) {
    current = url.replace(/\/$/, '');
    orgId = org || '';
    waiting.splice(0).forEach((fn) => { try { fn(current); } catch (e) { /* ignore */ } });
}

export class NotSetUpError extends Error {}

/** Connector: org ID from Salesforce -> the customer's bridge (directory mode only). */
export async function findBridge(organizationId) {
    if (!usesDirectory()) { orgId = org15(organizationId); forget(); return current; }
    const org = org15(organizationId);
    if (!org) throw new NotSetUpError('Salesforce did not pass its org ID to the phone');
    let r;
    try {
        r = await fetch(`${DIRECTORY}${encodeURIComponent(org)}.json`, { cache: 'no-store' });
    } catch (e) {
        throw new Error(`Telqon directory not reachable: ${e.message}`);
    }
    if (r.status === 403 || r.status === 404) {
        throw new NotSetUpError(`This Salesforce org (${org}) isn't set up with Telqon yet. Ask Telqon support to add it.`);
    }
    if (!r.ok) throw new Error(`Telqon directory answered HTTP ${r.status}`);
    const entry = await r.json().catch(() => ({}));
    const url = String(entry.bridge || '');
    if (!/^https:\/\/[^/]+$|^http:\/\/(localhost|127\.0\.0\.1)(:\d+)?$/.test(url.replace(/\/$/, ''))) {
        throw new Error('Telqon directory entry has no valid bridge address');
    }
    use(url, org);
    save({ bridge: current, org });
    return current;
}

function save(v) {
    try { localStorage.setItem(STORE_KEY, JSON.stringify(v)); } catch (e) { /* storage blocked */ }
}

function forget() {
    try { localStorage.removeItem(STORE_KEY); } catch (e) { /* storage blocked */ }
}

/** Connector: no bridge for this org; the login page shows why. */
export function rememberProblem(message) {
    if (usesDirectory()) save({ error: message });
}

/** Login page: why there is no bridge, if the connector said so. */
export let problem = '';

/** Login page: the bridge the connector found (same origin, so same storage), else ask the connector frame. */
export async function bridgeForLoginPage(channelName) {
    if (!usesDirectory()) return current;
    try {
        const saved = JSON.parse(localStorage.getItem(STORE_KEY) || 'null');
        if (saved && saved.bridge) { use(saved.bridge, saved.org); return current; }
        if (saved && saved.error) { problem = saved.error; return ''; }
    } catch (e) { /* storage blocked: ask the connector */ }
    if (typeof BroadcastChannel === 'undefined') { problem = 'This browser can\'t open the sign-in page; use a current browser.'; return ''; }
    const ch = new BroadcastChannel(channelName);
    const answer = new Promise((resolve) => {
        const t = setTimeout(() => resolve(null), 3000);
        ch.onmessage = (e) => {
            if (e.data && e.data.type === 'BRIDGE') { clearTimeout(t); resolve(e.data); }
        };
    });
    ch.postMessage({ type: 'WHICH_BRIDGE' });
    const got = await answer;
    ch.close();
    if (got && got.bridge) use(got.bridge, got.org);
    else problem = (got && got.error) || 'Open the phone from Salesforce first, then sign in.';
    return current;
}

export function org15(id) {
    return String(id || '').trim().slice(0, 15);
}
